<template>
  <div class="sap-page">
    <div class="sap-header">
      <h1 class="sap-title">店家資料</h1>
    </div>


    <div v-if="loading" class="sap-state">
      <span class="sap-spinner" /> 載入中…
    </div>
    <div v-else-if="loadError" class="sap-alert sap-alert--error">{{ loadError }}</div>

    <template v-else>
      <!-- 新手引導橫幅 -->
      <div v-if="showOnboard" class="sap-onboard-bar">
        <div class="sap-onboard-bar__steps">
          <span class="sap-onboard-bar__step sap-onboard-bar__step--active">① 店家資料</span>
          <span class="sap-onboard-bar__sep">›</span>
          <span class="sap-onboard-bar__step">② 營業時間</span>
          <span class="sap-onboard-bar__sep">›</span>
          <span class="sap-onboard-bar__step">③ 服務設定</span>
          <span class="sap-onboard-bar__sep">›</span>
          <span class="sap-onboard-bar__step">④ 新增時段</span>
        </div>
        <p class="sap-onboard-bar__hint">👇 從這裡開始！填寫完整的店家資料，完成後點「儲存資料」繼續下一步</p>
      </div>

      <div class="sap-card">

        <!-- 封面圖 -->
        <div class="sap-field">
          <label class="sap-label">封面圖</label>

          <div class="sap-img-actions">
            <label class="sap-btn sap-btn--secondary sap-btn--sm sap-img-upload-btn">
              <input
                type="file"
                accept="image/*"
                style="display:none"
                @change="onImageFileChange"
              />
              📷 選擇圖片上傳
            </label>
            <span class="sap-img-or">或</span>
            <input
              v-model="form.cover_image"
              class="sap-input sap-img-url-input"
              placeholder="貼上圖片網址 https://…"
              @input="imgInfo = null; previewError = false"
            />
          </div>
          <p class="sap-img-hint">上傳限制：JPG / PNG / WEBP，單檔不超過 8MB；系統自動壓縮輸出至 400KB 以內</p>

          <div v-if="imageUploading" class="sap-img-uploading">
            <span class="sap-spinner sap-spinner--sm" /> 上傳中…
          </div>

          <template v-if="form.cover_image && !imageUploading">
            <div v-if="previewError" class="sap-img-err">⚠ 無法載入此圖片網址</div>
            <div v-else class="sap-preview-wrap">
              <div class="sap-preview-frame">
                <img
                  :src="form.cover_image"
                  alt="封面預覽"
                  class="sap-preview-img"
                  @load="onPreviewLoad"
                  @error="previewError = true"
                />
                <div v-if="imgInfo" class="sap-preview-badge">
                  {{ imgInfo.width }} × {{ imgInfo.height }} px
                  <span v-if="imgInfo.width < 600 || imgInfo.height < 600" class="sap-preview-warn">
                    ⚠ 建議至少 600 × 600
                  </span>
                </div>
              </div>
              <p class="sap-preview-hint">↑ 前台顯示效果（IG 正方形 1:1）</p>
            </div>
          </template>
        </div>

        <!-- 店名 -->
        <div class="sap-field">
          <label class="sap-label">店名 <span class="sap-req">*</span></label>
          <input
            v-model="form.name"
            class="sap-input"
            :class="{ 'sap-input--error': errors.name }"
            placeholder="請輸入店名"
          />
          <p v-if="errors.name" class="sap-field-err">{{ errors.name }}</p>
        </div>

        <!-- 地址 -->
        <div class="sap-field">
          <label class="sap-label">地址 <span class="sap-req">*</span></label>
          <input
            v-model="form.address"
            class="sap-input"
            :class="{ 'sap-input--error': errors.address }"
            placeholder="請輸入地址"
          />
          <p v-if="errors.address" class="sap-field-err">{{ errors.address }}</p>
        </div>

        <!-- 電話 -->
        <div class="sap-field">
          <label class="sap-label">電話 <span class="sap-req">*</span></label>
          <input
            v-model="form.phone"
            class="sap-input"
            :class="{ 'sap-input--error': errors.phone }"
            placeholder="請輸入電話"
          />
          <p v-if="errors.phone" class="sap-field-err">{{ errors.phone }}</p>
        </div>

        <!-- 信箱 -->
        <div class="sap-field">
          <label class="sap-label">登入信箱</label>
          <input
            v-model="form.email"
            type="email"
            class="sap-input"
            :class="{ 'sap-input--error': errors.email }"
            placeholder="請輸入信箱"
          />
          <p v-if="errors.email" class="sap-field-err">{{ errors.email }}</p>
          <p v-if="form.email !== originalEmail" class="sap-field-hint" style="color:#e67e22">⚠ 信箱變更後將自動登出，請用新信箱重新登入</p>
        </div>

        <!-- LINE ID -->
        <div class="sap-field">
          <label class="sap-label">LINE ID</label>
          <input
            v-model="form.line_id"
            class="sap-input"
            placeholder="例：@jiliskin（選填）"
          />
          <p class="sap-field-hint">請填寫官方帳號 ID（以 @ 開頭），填寫後顧客可在前台直接點擊 LINE 聯絡</p>
        </div>

        <!-- 簡介 -->
        <div class="sap-field">
          <label class="sap-label">店家簡介</label>
          <textarea
            v-model="form.description"
            class="sap-input sap-textarea"
            rows="4"
            placeholder="選填，顯示在前台店家頁面"
          />
        </div>

        <!-- Google Maps 連結 -->
        <div class="sap-field">
          <label class="sap-label">Google Maps 導航連結</label>
          <input
            v-model="form.map_url"
            class="sap-input"
            placeholder="貼上 Google Maps 分享連結 https://maps.app.goo.gl/…"
          />
          <p class="sap-field-hint">開啟 Google Maps 找到店家 → 點「分享」→ 複製連結貼上</p>
        </div>

        <!-- 服務分類 -->
        <div class="sap-field">
          <label class="sap-label">服務分類</label>
          <div class="sap-checkbox-group">
            <label
              v-for="c in categories"
              :key="c.id"
              class="sap-checkbox"
            >
              <input
                type="checkbox"
                :value="c.id"
                v-model="form.category_ids"
              />
              <span>{{ c.name }}</span>
            </label>
          </div>
        </div>

        <p v-if="saveError" class="sap-alert sap-alert--error">{{ saveError }}</p>
        <p v-if="saveOk"    class="sap-alert sap-alert--success">✓ 儲存成功</p>

        <!-- 儲存後：成功提示（引導由側欄漢堡選單負責） -->
        <div v-if="showNextStep" class="sap-next-step">
          <span class="sap-next-step__ok">✅ 資料已儲存！</span>
          <span class="sap-next-step__label">請點左上角選單繼續下一步</span>
        </div>

        <div class="sap-footer">
          <button
            class="sap-btn sap-btn--primary"
            :disabled="saving"
            @click="save"
          >
            <span v-if="saving" class="sap-spinner sap-spinner--sm sap-spinner--white" />
            {{ saving ? '儲存中…' : '儲存資料' }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useStoreAdminStore } from '@/store/storeAdminStore'

const storeAdminStore = useStoreAdminStore()
const router          = useRouter()

// ── 新手引導 ────────────────────────────────────────
const onboardKey    = computed(() => `jili_ob_p_${storeAdminStore.storeId || ''}`)
const showOnboard   = ref(false)
const showNextStep  = ref(false)
const updateObStep  = inject('updateObStep', () => {})

const loading    = ref(false)
const loadError  = ref('')
const saving     = ref(false)
const saveError  = ref('')
const saveOk     = ref(false)
const imageUploading = ref(false)
const imgInfo        = ref(null)
const previewError   = ref(false)

const categories    = ref([])
const originalEmail = ref('')
const form   = ref({ name: '', address: '', phone: '', email: '', line_id: '', line_channel_id: '', line_channel_secret: '', line_confirm_message: '', description: '', cover_image: '', map_url: '', category_ids: [] })
const errors = ref({})

function authHeader() {
  return { Authorization: `Bearer ${storeAdminStore.token}` }
}

async function load() {
  loading.value   = true
  loadError.value = ''
  try {
    const [profileRes, catRes] = await Promise.all([
      axios.get('/api/store-admin/profile', { headers: authHeader() }),
      axios.get('/api/booking/categories'),
    ])
    categories.value = catRes.data.categories || []
    if (profileRes.data.success && profileRes.data.store) {
      const s = profileRes.data.store
      originalEmail.value = s.email ?? ''
      form.value = {
        name:               s.name               ?? '',
        address:            s.address            ?? '',
        phone:              s.phone              ?? '',
        email:              s.email              ?? '',
        line_id:            s.line_id            ?? '',
        line_channel_id:    s.line_channel_id    ?? '',
        line_channel_secret:s.line_channel_secret?? '',
        line_confirm_message: s.line_confirm_message ?? '',
        description:        s.description        ?? '',
        cover_image:        s.cover_image        ?? '',
        map_url:            s.map_url            ?? '',
        category_ids:       s.category_ids       ?? [],
      }
    }
    // 新手引導：若未完成過儲存，顯示引導橫幅
    if (!localStorage.getItem(onboardKey.value)) showOnboard.value = true
  } catch (e) {
    loadError.value = e.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

async function onImageFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return

  // 前端先攔截：超過 8 MB 直接拒絕，省去上傳流量
  const MAX_MB   = 8
  const MAX_BYTES = MAX_MB * 1024 * 1024
  if (file.size > MAX_BYTES) {
    saveError.value = `圖片大小 ${(file.size / 1024 / 1024).toFixed(1)} MB，超過 ${MAX_MB} MB 限制，請壓縮後再上傳`
    e.target.value = ''
    return
  }

  imageUploading.value = true
  imgInfo.value        = null
  previewError.value   = false
  try {
    const fd = new FormData()
    fd.append('file', file)
    const res = await axios.post('/api/store-admin/profile/image', fd, { headers: authHeader() })
    if (res.data.success) {
      form.value.cover_image = res.data.url
      if (res.data.width && res.data.height) {
        imgInfo.value = { width: res.data.width, height: res.data.height }
      }
    }
  } catch (err) {
    saveError.value = err.response?.data?.message || '圖片上傳失敗'
  } finally {
    imageUploading.value = false
    e.target.value = ''
  }
}

function onPreviewLoad(e) {
  if (!imgInfo.value) {
    imgInfo.value = { width: e.target.naturalWidth, height: e.target.naturalHeight }
  }
}

function validate() {
  const e = {}
  if (!form.value.name.trim())    e.name    = '請輸入店名'
  if (!form.value.address.trim()) e.address = '請輸入地址'
  if (!form.value.phone.trim())   e.phone   = '請輸入電話'
  if (!form.value.email.trim())   e.email   = '請輸入信箱'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) e.email = '信箱格式錯誤'
  errors.value = e
  return Object.keys(e).length === 0
}

async function save() {
  if (!validate()) return
  const emailChanged = form.value.email.trim() !== originalEmail.value
  saving.value    = true
  saveError.value = ''
  saveOk.value    = false
  try {
    await axios.put('/api/store-admin/profile', {
      name:               form.value.name.trim(),
      address:            form.value.address.trim(),
      phone:              form.value.phone.trim(),
      email:              form.value.email.trim(),
      line_id:            (() => { const v = form.value.line_id.trim(); if (!v) return null; return v.startsWith('@') ? v : '@' + v })(),
      line_channel_id:    form.value.line_channel_id.trim()     || null,
      line_channel_secret:form.value.line_channel_secret.trim() || null,
      line_confirm_message: form.value.line_confirm_message.trim() || null,
      description:        form.value.description.trim()         || null,
      cover_image:        form.value.cover_image.trim()         || null,
      map_url:            form.value.map_url.trim()             || null,
      category_ids:       form.value.category_ids,
    }, { headers: authHeader() })
    if (emailChanged) {
      // 信箱已變更，清除登入狀態並跳回登入頁
      storeAdminStore.logout?.()
      router.replace({ name: 'StoreAdminLogin', query: { msg: 'email_changed' } })
      return
    }
    saveOk.value = true
    setTimeout(() => { saveOk.value = false }, 3000)
    // 新手引導：標記完成，通知 Layout 更新步驟
    localStorage.setItem(onboardKey.value, '1')
    showOnboard.value  = false
    showNextStep.value = true
    updateObStep()
  } catch (e) {
    saveError.value = e.response?.data?.message || '儲存失敗，請稍後再試'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
/* ── 新手引導橫幅 ──────────────────────────────── */
.sap-onboard-bar {
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  border: 1.5px solid #93c5fd;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
  max-width: 560px;
}
.sap-onboard-bar__steps {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.sap-onboard-bar__step {
  font-size: 0.82rem;
  color: #93c5fd;
  font-weight: 600;
}
.sap-onboard-bar__step--active { color: #1d4ed8; }
.sap-onboard-bar__sep {
  color: #f97316;
  font-size: 1.2rem;
  font-weight: 700;
  animation: sap-sep-flash 0.8s ease-in-out infinite;
}
@keyframes sap-sep-flash {
  0%, 100% { opacity: 1;   transform: translateX(0); }
  50%       { opacity: 0.3; transform: translateX(3px); }
}
.sap-onboard-bar__hint { font-size: 0.88rem; color: #1e40af; margin: 0; }

/* ── 儲存後下一步 ──────────────────────────────── */
.sap-next-step {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}
.sap-next-step__ok    { color: #16a34a; font-weight: 700; font-size: 0.92rem; }
.sap-next-step__label { font-size: 0.88rem; color: #374151; }
.sap-next-step__btn {
  background: #2563eb;
  color: #fff;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s;
  white-space: nowrap;
}
.sap-next-step__btn:hover { background: #1d4ed8; }

.sap-page { padding: 24px; max-width: 560px; }
.sap-header { margin-bottom: 16px; }
.sap-title  { font-size: 1.4rem; font-weight: 700; color: #2c3e50; margin: 0; }


.sap-state {
  display: flex; align-items: center; gap: 10px;
  color: #7f8c8d; padding: 40px 0;
}

.sap-card {
  background: #fff; border: 1px solid #e8ecf0;
  border-radius: 12px; padding: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

/* 欄位 */
.sap-field    { margin-bottom: 18px; }
.sap-label    { display: block; font-size: 0.87rem; font-weight: 500; color: #555; margin-bottom: 6px; }
.sap-req      { color: #e74c3c; }
.sap-field-err{ font-size: 0.8rem; color: #e74c3c; margin: 3px 0 0; }

.sap-input {
  width: 100%; padding: 9px 12px;
  border: 1px solid #dce1e7; border-radius: 8px;
  font-size: 0.92rem; background: #fff;
  box-sizing: border-box; transition: border-color 0.15s;
  -webkit-appearance: none;
}
.sap-input:focus { outline: none; border-color: #2c3e50; box-shadow: 0 0 0 3px rgba(44,62,80,0.07); }
.sap-input--error { border-color: #e74c3c; }
.sap-textarea { resize: vertical; min-height: 88px; }

/* 封面圖上傳 */
.sap-img-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sap-img-or      { color: #aaa; font-size: 0.85rem; flex-shrink: 0; }
.sap-img-url-input   { flex: 1; min-width: 180px; }
.sap-img-upload-btn  { cursor: pointer; flex-shrink: 0; }
.sap-img-uploading {
  display: flex; align-items: center; gap: 8px;
  margin-top: 8px; font-size: 0.85rem; color: #7f8c8d;
}
.sap-img-err { margin-top: 8px; font-size: 0.82rem; color: #e74c3c; }
.sap-img-hint, .sap-field-hint { margin: 5px 0 0; font-size: 0.78rem; color: #aaa; line-height: 1.6; }

/* 付費功能 badge */
.sap-label-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
  background: #27ae60;
  border-radius: 20px;
  vertical-align: middle;
  letter-spacing: 0.02em;
}

/* LINE Channel Token */
.sap-token-wrap  { display: flex; align-items: center; gap: 8px; }
.sap-token-input { flex: 1; font-family: monospace; font-size: 0.82rem; letter-spacing: 0.02em; }
.sap-token-eye {
  flex-shrink: 0; padding: 8px 12px; font-size: 0.8rem;
  border: 1px solid #dce1e7; border-radius: 8px;
  background: #f5f7fa; color: #555; cursor: pointer;
  transition: background 0.15s;
}
.sap-token-eye:hover { background: #eaeff5; }

/* 預覽 1:1 */
.sap-preview-wrap  { margin-top: 12px; display: flex; flex-direction: column; gap: 4px; }
.sap-preview-frame {
  position: relative; width: 160px; aspect-ratio: 1 / 1;
  border-radius: 10px; overflow: hidden;
  border: 2px solid #dce1e7; background: #f4f6f9;
}
.sap-preview-img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
.sap-preview-badge {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: rgba(0,0,0,0.62); color: #fff;
  font-size: 0.68rem; padding: 4px 6px; line-height: 1.5;
}
.sap-preview-warn  { color: #f39c12; display: block; }
.sap-preview-hint  { font-size: 0.78rem; color: #95a5a6; margin: 0; }

/* 提示訊息 */
.sap-alert { border-radius: 7px; padding: 10px 14px; font-size: 0.87rem; margin-top: 4px; }
.sap-alert--error   { background: #fdedec; border: 1px solid #f5b7b1; color: #c0392b; }
.sap-alert--success { background: #eafaf1; border: 1px solid #a9dfbf; color: #1e8449; }

/* 服務分類 */
.sap-checkbox-group {
  display: flex; flex-wrap: wrap; gap: 10px 20px; margin-top: 6px;
}
.sap-checkbox {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.9rem; cursor: pointer; color: #444;
}
.sap-checkbox input[type="checkbox"] { cursor: pointer; }

/* Footer */
.sap-footer { margin-top: 24px; display: flex; justify-content: flex-end; }

/* 按鈕 */
.sap-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 16px; border-radius: 7px; border: none;
  font-size: 0.9rem; font-weight: 500; cursor: pointer;
  transition: background 0.15s; white-space: nowrap;
}
.sap-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.sap-btn--sm      { padding: 6px 12px; font-size: 0.82rem; }
.sap-btn--primary { background: #2c3e50; color: #fff; }
.sap-btn--primary:hover:not(:disabled) { background: #34495e; }
.sap-btn--secondary { background: #fff; color: #2c3e50; border: 1px solid #dce1e7; }
.sap-btn--secondary:hover:not(:disabled) { background: #f4f6f9; }

/* Spinner */
.sap-spinner {
  display: inline-block; width: 22px; height: 22px;
  border: 3px solid rgba(0,0,0,0.1); border-top-color: #7f8c8d;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
.sap-spinner--sm    { width: 14px; height: 14px; border-width: 2px; }
.sap-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 520px) {
  .sap-page { padding: 14px; }
}
</style>
